import { useEffect, useMemo, useRef, useState } from 'react'
import { magasin } from '../lib/client/store.ts'
import { emojiProduit, useMagasin, useRayons, type Lieu, type Produit } from '../lib/client/hooks.ts'
import { chercher, normalise, proches } from '../lib/search.ts'
import { Sheet } from '../components/Sheet.tsx'
import { annoncer } from '../components/Toast.tsx'
import { Scanner, type Resultat as ResultatScan } from '../components/Scanner.tsx'
import { Coche, Croix } from '../components/Icone.tsx'

/**
 * Ajout rapide.
 *
 * Deux décisions portent tout : le champ GARDE LE FOCUS après chaque ajout —
 * c'est toute la définition de « saisie rapide » —, et les articles ajoutés
 * s'empilent en pastilles sous le champ plutôt qu'en toasts, parce qu'on en
 * aurait huit d'affilée.
 *
 * Une feuille et non une page : on voit sa liste derrière, ce qui dit où
 * l'article va atterrir.
 */
export function AjoutRapide({
  ouverte,
  onFermer,
  saisieInitiale = '',
}: {
  ouverte: boolean
  onFermer: () => void
  /** Terme déjà tapé ailleurs — la recherche de l'inventaire le transmet
   *  quand elle ne trouve rien, pour ne pas le faire retaper. */
  saisieInitiale?: string
}) {
  const { etat } = useMagasin()
  const rayons = useRayons()
  const [saisie, setSaisie] = useState(saisieInitiale)
  const [destination, setDestination] = useState<'inventaire' | 'courses'>('inventaire')
  const [ajoutes, setAjoutes] = useState<Array<{ id: string; nom: string }>>([])
  const [scanner, setScanner] = useState(false)
  const [ean, setEan] = useState<string | null>(null)
  const champ = useRef<HTMLInputElement>(null)

  /*
   * La feuille reste MONTÉE entre deux ouvertures : l'état initial ne suffit
   * donc pas à reprendre le terme transmis, il faut le réappliquer à chaque
   * ouverture. Sans ça, le mot ne passerait que la toute première fois —
   * le genre de défaut qui se manifeste au deuxième essai, donc chez
   * l'utilisateur et pas dans le test.
   */
  useEffect(() => {
    if (ouverte && saisieInitiale) setSaisie(saisieInitiale)
  }, [ouverte, saisieInitiale])

  const produits = useMemo(
    () =>
      ([...etat.catalogItems.values()] as unknown as Produit[]).filter(
        (p) => !(p as unknown as { deletedAt?: unknown }).deletedAt,
      ),
    [etat],
  )
  const lieux = useMemo(
    () =>
      ([...etat.locations.values()] as unknown as Lieu[])
        .filter((l) => !(l as unknown as { deletedAt?: unknown }).deletedAt)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [etat],
  )

  /*
   * Balayage linéaire en mémoire sur quelques centaines d'entrées : moins
   * d'une milliseconde, aucune dépendance, aucun index à maintenir. Et c'est
   * OBLIGATOIREMENT du JavaScript, puisque la recherche qui compte — celle du
   * supermarché — doit fonctionner hors-ligne.
   */
  /*
   * « Nouveau produit · Frigo » plutôt qu'une phrase.
   *
   * La formulation naturelle demanderait la bonne préposition — « au
   * frigo », « à la cave », « dans la corbeille à fruits », « en salle de
   * bain » — et les lieux sont modifiables par le foyer, donc aucune règle
   * ne tient. Un séparateur évite le piège sans rien perdre en clarté.
   */
  /*
   * L'étiquette annonce l'emplacement RÉEL, devinette comprise.
   *
   * Elle affichait le premier emplacement en dur. C'était vrai tant que
   * l'ajout y envoyait tout ; depuis qu'un produit neuf hérite de
   * l'emplacement de son plus proche voisin, un « Nouveau produit · Frigo »
   * au-dessus d'un sachet de cumin serait un mensonge — et le genre de
   * mensonge qu'on ne découvre qu'en cherchant le cumin au frigo.
   */
  const lieuDevine = useMemo(() => {
    const nom = saisie.trim()
    if (!nom) return lieux[0]
    const voisin = proches(
      produits
        .filter((p) => p.defaultLocationId)
        .map((p) => ({ nom: p.name, search: p.search, tier: p.tier, lieu: p.defaultLocationId! })),
      nom,
      1,
    )[0]
    return lieux.find((l) => l.id === voisin?.lieu) ?? lieux[0]
  }, [saisie, produits, lieux])

  const destinationLabel =
    destination === 'courses' ? 'Courses' : (lieuDevine?.name ?? 'Inventaire')

  const resultats = useMemo(
    () => chercher(produits.map((p) => ({ ...p, nom: p.name })), saisie, 8),
    [produits, saisie],
  )

  function ajouter(produit: Produit) {
    if (destination === 'courses') {
      magasin.dispatch('list.add', { id: magasin.id(), catalogItemId: produit.id, reason: 'manuel' })
    } else {
      const lieu = produit.defaultLocationId ?? lieux[0]?.id
      if (!lieu) return
      magasin.dispatch('item.create', {
        id: magasin.id(),
        catalogItemId: produit.id,
        locationId: lieu,
        state: 'en_stock',
      })
    }
    setAjoutes((a) => [{ id: magasin.id(), nom: produit.name }, ...a].slice(0, 12))
    setSaisie('')
    // Le focus NE PART PAS : c'est ce qui permet d'enchaîner dix ajouts.
    champ.current?.focus()
  }

  /**
   * Un code scanné, PENDANT que la caméra reste ouverte.
   *
   * Trois issues, et c'est la troisième qui décide de sortir :
   *  - déjà au catalogue du foyer → on ajoute et on ENCHAÎNE ;
   *  - trouvé chez Open Food Facts → on crée le produit, on ajoute, on
   *    ENCHAÎNE ;
   *  - inconnu, ou réseau absent → il faut un nom, donc le clavier : on sort
   *    en gardant l'EAN. C'est ce qui fait que scanner une marque
   *    distributeur sert quand même à quelque chose — le code est mémorisé,
   *    le prochain scan le trouvera.
   */
  async function codeScanne(code: string): Promise<ResultatScan> {
    try {
      const r = await fetch(`/api/codebarres/${code}`)
      const d = (await r.json()) as {
        source: string
        catalogItemId?: string
        nom?: string | null
      }

      // Connu du foyer : le nom qu'on lui donne prime sur celui d'Open Food Facts.
      if (d.source === 'catalogue' && d.catalogItemId) {
        const produit = produits.find((p) => p.id === d.catalogItemId)
        if (produit) {
          const deja = dejaPresent(produit.id)
          ajouter(produit)
          return { statut: deja ? 'deja' : 'ajoute', nom: produit.name }
        }
      }

      if (d.nom) {
        // Un produit du catalogue porte-t-il déjà ce nom ? On ne veut pas
        // créer un doublon parce qu'Open Food Facts formule autrement.
        const existant = produits.find(
          (p) => normalise(p.name) === normalise(d.nom!),
        )
        if (existant) {
          const deja = dejaPresent(existant.id)
          ajouter(existant)
          return { statut: deja ? 'deja' : 'ajoute', nom: existant.name }
        }

        const id = magasin.id()
        magasin.dispatch('catalog.create', { id, name: d.nom, barcode: code })
        rangerNouveau(id)
        setAjoutes((a) => [{ id: magasin.id(), nom: d.nom! }, ...a].slice(0, 12))
        return { statut: 'ajoute', nom: d.nom }
      }

      setEan(code)
      setSaisie('')
      return { statut: 'inconnu' }
    } catch {
      setEan(code)
      return { statut: 'inconnu' }
    }
  }

  function dejaPresent(catalogItemId: string): boolean {
    if (destination === 'courses') {
      return [...etat.shoppingItems.values()].some(
        (l) => !l.deletedAt && !l.tripId && l.catalogItemId === catalogItemId,
      )
    }
    return [...etat.inventoryItems.values()].some(
      (i) => !i.deletedAt && i.catalogItemId === catalogItemId,
    )
  }

  /**
   * Range un produit tout juste créé, à sa destination courante.
   *
   * L'emplacement est celui du PRODUIT, pas le premier de la liste. Le
   * raccourci « premier emplacement » envoyait tout au frigo : un foyer
   * s'est retrouvé avec ses épices, ses farines et ses conserves au
   * réfrigérateur après une série de scans. Un produit tout juste créé en
   * hérite un, deviné d'après son plus proche voisin connu.
   */
  function lieuDe(catalogItemId: string): string | undefined {
    const p = magasin.lire().etat.catalogItems.get(catalogItemId) as
      | { defaultLocationId?: string | null }
      | undefined
    return p?.defaultLocationId ?? lieux[0]?.id
  }

  function rangerNouveau(catalogItemId: string) {
    if (destination === 'courses') {
      magasin.dispatch('list.add', { id: magasin.id(), catalogItemId, reason: 'manuel' })
      return
    }
    const locationId = lieuDe(catalogItemId)
    if (!locationId) return
    magasin.dispatch('item.create', {
      id: magasin.id(),
      catalogItemId,
      locationId,
      state: 'en_stock',
    })
  }

  function creerEtAjouter() {
    const nom = saisie.trim()
    if (!nom) return

    /*
     * RÉSOUDRE AVANT DE CRÉER.
     *
     * `catalog.create` est idempotent par nom : si le produit existe déjà,
     * il ne crée rien — et ne le dit pas. On enchaînait pourtant avec
     * l'identifiant qu'on venait de tirer, qui ne désignait alors aucun
     * produit : la ligne s'affichait « Article », sans nom ni emoji.
     *
     * Ça se déclenche dès qu'on tape un nom déjà au catalogue au lieu de
     * choisir la suggestion — c'est-à-dire souvent. Le chemin du scan
     * faisait déjà cette résolution ; celui de la saisie l'avait oubliée.
     */
    const existant = produits.find((p) => normalise(p.name) === normalise(nom))
    const id = existant?.id ?? magasin.id()
    if (!existant) magasin.dispatch('catalog.create', { id, name: nom, barcode: ean ?? undefined })
    setEan(null)
    // Même chemin que le scan : l'emplacement vient du produit.
    rangerNouveau(id)
    setAjoutes((a) => [{ id: magasin.id(), nom }, ...a].slice(0, 12))
    setSaisie('')
    champ.current?.focus()
  }

  return (
    <Sheet ouverte={ouverte} onFermer={onFermer} titre="Ajout rapide">
      <div className="mb-3 flex h-[34px] rounded-sm bg-surface-sunk p-[3px]">
        {(
          [
            ['inventaire', 'Inventaire'],
            ['courses', 'Courses'],
          ] as const
        ).map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => setDestination(v)}
            aria-pressed={destination === v}
            className={`pressable flex-1 rounded-[7px] text-meta ${
              destination === v ? 'bg-surface font-semibold text-ink shadow-row' : 'text-muted'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {ean && (
        <div className="mb-2 flex items-start gap-2 rounded-sm bg-surface-sunk px-3 py-2">
          <p className="min-w-0 flex-1 text-meta text-muted">
            Code-barres <span className="tnum select-all">{ean}</span> non reconnu. Donne-lui un
            nom pour l'enregistrer.
          </p>
          {/* Sans cette sortie, un code inconnu restait épinglé en haut de la
              feuille sans aucun moyen de s'en défaire. */}
          <button
            type="button"
            aria-label="Ignorer ce code-barres"
            onClick={() => {
              setEan(null)
              setSaisie('')
            }}
            className="pressable shrink-0 text-muted"
          >
            <Croix className="size-4" />
          </button>
        </div>
      )}

      <div className="flex gap-2">
      <input
        ref={champ}
        autoFocus
        value={saisie}
        onChange={(e) => setSaisie(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return
          // Action initiée au clavier : aucune animation, le champ se vide net.
          if (resultats[0]) ajouter(resultats[0] as unknown as Produit)
          else creerEtAjouter()
        }}
        placeholder="Chercher un aliment…"
        /* Le correcteur français massacre « chiffonnade » et « rillettes ».
           inputMode=search et enterKeyHint=done donnent le bon clavier. */
        autoCorrect="off"
        autoCapitalize="sentences"
        spellCheck={false}
        autoComplete="off"
        inputMode="search"
        enterKeyHint="done"
        className="h-[48px] min-w-0 flex-1 rounded-sm bg-surface-sunk px-3 text-field text-ink outline-none"
      />
      <button
        type="button"
        aria-label="Scanner un code-barres"
        onClick={() => setScanner(true)}
        className="pressable flex size-[48px] shrink-0 items-center justify-center rounded-sm bg-surface-sunk text-ink"
      >
        <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
          <path d="M4 7V5a1 1 0 0 1 1-1h2M20 7V5a1 1 0 0 0-1-1h-2M4 17v2a1 1 0 0 0 1 1h2M20 17v2a1 1 0 0 1-1 1h-2M7 8v8M10 8v8M13.5 8v8M17 8v8" />
        </svg>
      </button>
      </div>

      <div className="mt-2">
        {/*
          L'ajout d'un produit inconnu passe EN PREMIER et en bouton plein.
          C'était un lien texte de 52 px de haut placé après les suggestions :
          après un scan, le nom fait souvent plus d'une ligne, le texte
          débordait de sa hauteur fixe, et l'action principale se retrouvait
          au ras du bas de l'écran — illisible et impossible à viser. Or
          après un scan, c'est LA seule chose à faire.
        */}
        {saisie.trim() && (
          <button
            type="button"
            onClick={creerEtAjouter}
            className="pressable mb-2 flex min-h-[52px] w-full items-center gap-3 rounded-sm bg-accent px-3 py-2 text-left text-accent-ink"
          >
            {/* SVG et non « ＋ » : le plus pleine chasse manque à beaucoup de
                polices et rend un carré vide. Déjà corrigé sur la barre
                d'onglets, la même erreur s'était glissée ici. */}
            <svg aria-hidden viewBox="0 0 24 24" className="size-5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span className="min-w-0 flex-1">
              <span className="block text-meta opacity-80">
                Nouveau produit · {destinationLabel}
              </span>
              {/* line-clamp-2 : un nom d'étiquette peut être long, mais il ne
                  doit jamais repousser le bouton hors de l'écran. */}
              <span className="block line-clamp-2 text-head">{saisie.trim()}</span>
            </span>
          </button>
        )}

        {resultats.length > 0 && (
          <p className="mb-1 px-1 text-label uppercase text-muted">Déjà au catalogue</p>
        )}

        {resultats.map((p) => {
          const produit = p as unknown as Produit
          const lieu = lieux.find((l) => l.id === produit.defaultLocationId)
          return (
            <button
              key={produit.id}
              type="button"
              onClick={() => ajouter(produit)}
              className="pressable flex min-h-[52px] w-full items-center gap-3 rounded-sm px-2 py-1 text-left"
            >
              <span aria-hidden className="w-6 text-center text-[17px]">{emojiProduit(produit, rayons) ?? '·'}</span>
              <span className="flex-1 truncate text-body text-ink">{produit.name}</span>
              {destination === 'inventaire' && lieu && (
                <span className="text-meta text-muted">{lieu.name}</span>
              )}
            </button>
          )
        })}

      </div>

      {ajoutes.length > 0 && (
        <>
          <div className="mt-4 border-t border-line pt-3">
            {/*
              « Déjà enregistré » et non « Ajoutés » : il n'y a AUCUNE étape
              de validation, chaque ajout part immédiatement dans le journal
              de synchronisation. Ne pas le dire laissait croire qu'il
              fallait valider — c'est très exactement la question qui a été
              posée après dix scans.
            */}
            <p className="mb-2 flex items-center gap-1.5 text-label uppercase text-muted">
              <Coche className="size-3.5 text-ok" />
              Déjà enregistré · {destinationLabel}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {ajoutes.map((a) => (
                <span
                  key={a.id}
                  className="rounded-full bg-ok-soft px-2.5 py-1 text-meta text-ink"
                  style={{ animation: 'pop 140ms var(--ease-out)' }}
                >
                  {a.nom}
                </span>
              ))}
            </div>
          </div>

          {/*
            Sortie explicite. La poignée de glissement reste le geste
            naturel, mais quand le contenu remplit la feuille il ne reste
            presque plus de voile à toucher, et rien n'indique la sortie.
          */}
          <button
            type="button"
            onClick={onFermer}
            className="pressable mt-4 h-[52px] w-full rounded-sm bg-accent text-head text-accent-ink"
          >
            Terminé · {ajoutes.length} article{ajoutes.length > 1 ? 's' : ''}
          </button>
        </>
      )}

      <Scanner
        ouvert={scanner}
        destination={destinationLabel}
        onCode={codeScanne}
        onFermer={(nb) => {
          setScanner(false)
          if (nb > 0) annoncer(`${nb} article${nb > 1 ? 's' : ''} scanné${nb > 1 ? 's' : ''}`)
          // Le champ reprend la main : si on est sorti pour nommer un produit
          // inconnu, le clavier doit être là sans un geste de plus.
          setTimeout(() => champ.current?.focus(), 100)
        }}
      />
    </Sheet>
  )
}
